// @flow
import React, {useState} from 'react';
import {Typography, Grid, Divider} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import CustomChip from '../../../../../elements/CustomChip';
import DangerIcon from '../../../../../elements/icons/DangerIcon';
import {colors} from '../../../../../assets/styles/colors';
import {formatDate} from '../../../../../utils/date';
import type {
  CustomerComplaint,
  CustomerComplaintState,
} from '../../../../../entities/customer-complaints';
import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';

type Props = {|
  open: boolean,
  complaint?: CustomerComplaint,
  onClose: () => void,
  onUpdateStatus: (
    complaintId: string,
    newStatus: CustomerComplaintState
  ) => void,
  isComplaintOverdue: (complaint: CustomerComplaint) => boolean,
|};

const useStyles = makeStyles(theme => ({
  modalContent: {
    maxWidth: 800,
    width: '100%',
    padding: theme.spacing(1),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1rem',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.5rem',
    color: colors.grey900,
  },
  correlativeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  overdueIcon: {
    color: colors.error,
    fontSize: '1.2rem',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.125rem',
    color: colors.grey900,
    // marginBottom: theme.spacing(2),
  },
  label: {
    fontSize: '0.875rem',
    color: colors.grey700,
    fontWeight: 500,
    // marginBottom: theme.spacing(0.5),
  },
  value: {
    fontSize: '0.875rem',
    color: colors.grey800,
    fontWeight: 400,
  },
  description: {
    backgroundColor: colors.grey100,
    padding: theme.spacing(2),
    borderRadius: '8px',
    fontSize: '0.875rem',
    color: colors.grey800,
    lineHeight: 1.5,
  },
  statusSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.grey50,
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  statusInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
}));

const getStatusColor = (status: CustomerComplaintState): string => {
  switch (status) {
    case 'RECEIVED':
      return 'warning';
    case 'SOLVED':
      return 'success';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: CustomerComplaintState): string => {
  switch (status) {
    case 'RECEIVED':
      return 'Recibido';
    case 'SOLVED':
      return 'Resuelto';
    default:
      return status;
  }
};

export default function ComplaintDetailModal(props: Props) {
  const {open, complaint, onClose, onUpdateStatus, isComplaintOverdue} = props;
  const classes = useStyles();

  const [statusBeingChanged, setStatusBeingChanged] = useState(false);

  if (!complaint) return null;

  const isOverdue = isComplaintOverdue(complaint);

  const handleStatusToggle = () => {
    setStatusBeingChanged(true);
    const newStatus = complaint.state === 'RECEIVED' ? 'SOLVED' : 'RECEIVED';
    onUpdateStatus(complaint.id, newStatus);

    // Reset the loading state after a short delay
    setTimeout(() => {
      setStatusBeingChanged(false);
    }, 500);
  };

  return (
    <CustomModal open={open} handleCloseModal={onClose} maxWidth="md">
      <div className={classes.modalContent}>
        <div className={classes.header}>
          <div className={classes.correlativeContainer}>
            {isOverdue && <DangerIcon className={classes.overdueIcon} />}
            <Typography className={classes.title}>
              Detalle del Reclamo {'#'}
              {String(complaint.externalId)?.padStart(6, '0')}
            </Typography>
          </div>
        </div>

        {/* Status Section */}
        <div className={classes.statusSection}>
          <div className={classes.statusInfo}>
            <Typography className={classes.label}>Estado actual:</Typography>
            <CustomChip
              label={getStatusLabel(complaint.state)}
              color={getStatusColor(complaint.state)}
              size="small"
            />
            {isOverdue && (
              <Typography variant="caption" style={{color: colors.error}}>
                (Vencido - Más de 10 días)
              </Typography>
            )}
          </div>
          <div className={classes.switchContainer}>
            <Typography className={classes.label}>
              {complaint.state === 'RECEIVED'
                ? 'Marcar como resuelto'
                : 'Revertir a recibido'}
            </Typography>
            <IosSwitch
              checked={complaint.state === 'SOLVED'}
              onChange={handleStatusToggle}
              disabled={statusBeingChanged}
              color="primary"
            />
          </div>
        </div>

        {/* Customer Information */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Información del Cliente
          </Typography>
          <Grid container spacing={2} className={classes.infoGrid}>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Identificador</Typography>
              <Typography className={classes.value}>
                {complaint.identifier}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Email</Typography>
              <Typography className={classes.value}>
                {complaint.email}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Teléfono</Typography>
              <Typography className={classes.value}>
                {complaint.phone}
              </Typography>
            </Grid>
          </Grid>
        </div>

        <Divider className={classes.divider} />

        {/* Complaint Information */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Información del Reclamo
          </Typography>
          <Grid container spacing={2} className={classes.infoGrid}>
            <Grid item xs={12}>
              <Typography className={classes.label}>
                Fecha de creación
              </Typography>
              <Typography className={classes.value}>
                {formatDate(new Date(complaint.createdAt), 'DD/MM/YYYY HH:mm')}
              </Typography>
            </Grid>

            {complaint.complaint.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Typography className={classes.label}>
                  {item.question}
                </Typography>
                <div className={classes.description}>{item.answer}</div>
              </Grid>
            ))}
          </Grid>
        </div>

        <Divider className={classes.divider} />

        <div className={classes.actions}>
          <CustomButton variant="contained" onClick={onClose}>
            Cerrar
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
}
